import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';

import {
  ArrowRight,
  CircleNotch,
  Coins,
  CreditCard,
  Lightning,
  Warning,
  X
} from '@phosphor-icons/react';
const CREDIT_PACKAGES = [
  { id: 'credits_2000', credits: '2,000', price: '$20', rawCredits: 2000 },
  { id: 'credits_5000', credits: '5,000', price: '$50', rawCredits: 5000 },
  { id: 'credits_12000', credits: '12,000', price: '$100', rawCredits: 12000, popular: true },
  { id: 'credits_70000', credits: '70,000', price: '$500', rawCredits: 70000 },
];

const InsufficientCreditsModal = ({ open, onClose, cost, balance, message }) => {
  const [purchasing, setPurchasing] = useState(null);

  const handleBuyCredits = async (packageId) => {
    setPurchasing(packageId);
    // Use original fetch to bypass the credit guard interceptor
    const safeFetch = window.__originalFetch || window.fetch;
    try {
      const response = await safeFetch(`${BACKEND_URL}/api/stripe/create-credit-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create checkout');
      }
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Credit purchase error:', error);
    } finally {
      setPurchasing(null);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-2xl rounded-3xl overflow-hidden"
            data-testid="insufficient-credits-modal"
          >
            {/* Gradient border effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#EC4899] via-[#6366F1] to-[#14F195] p-[1px] rounded-3xl">
              <div className="w-full h-full bg-[#0B1120] rounded-3xl" />
            </div>

            <div className="relative z-10 p-8">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-xl text-[#A0AEC0] hover:text-white hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                data-testid="close-credits-modal"
              >
                <X size={20} />
              </button>

              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#EC4899]/20 to-[#6366F1]/20 border border-[#EC4899]/30 flex items-center justify-center flex-shrink-0">
                  <Warning size={28} className="text-[#EC4899]" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1" data-testid="credits-modal-title">
                    Oops! You've run out of credits
                  </h2>
                  <p className="text-[#A0AEC0]">
                    {message || "Your credit balance is too low to complete this operation. Top up your credits to keep your AI agents running."}
                  </p>
                </div>
              </div>

              {/* Balance & Cost info */}
              {(balance > 0 || cost > 0) && (
                <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)]">
                  {balance >= 0 && (
                    <div className="flex items-center gap-2">
                      <Coins size={16} className="text-[#A0AEC0]" />
                      <span className="text-sm text-[#A0AEC0]">Balance:</span>
                      <span className="text-sm font-bold text-white" data-testid="credits-modal-balance">{balance} credits</span>
                    </div>
                  )}
                  {cost > 0 && (
                    <>
                      <div className="w-px h-6 bg-[rgba(255,255,255,0.1)]" />
                      <div className="flex items-center gap-2">
                        <Lightning size={16} className="text-[#EC4899]" />
                        <span className="text-sm text-[#A0AEC0]">Required:</span>
                        <span className="text-sm font-bold text-[#EC4899]" data-testid="credits-modal-cost">{cost} credits</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Credit Packages */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">Recharge your credits</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {CREDIT_PACKAGES.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => handleBuyCredits(pkg.id)}
                      disabled={purchasing === pkg.id}
                      className={`group relative p-4 rounded-2xl border transition-all text-left ${
                        pkg.popular
                          ? 'border-[rgba(99,102,241,0.5)] bg-[rgba(99,102,241,0.08)] hover:bg-[rgba(99,102,241,0.15)]'
                          : 'border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.06)]'
                      } hover:border-[rgba(99,102,241,0.4)]`}
                      data-testid={`credits-modal-buy-${pkg.id}`}
                    >
                      {pkg.popular && (
                        <span className="absolute -top-2 left-3 px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-full">
                          BEST VALUE
                        </span>
                      )}
                      <div className="text-xl font-bold text-white mb-0.5">{pkg.credits}</div>
                      <div className="text-xs text-[#A0AEC0] mb-3">credits</div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-[#14F195]">{pkg.price}</span>
                        {purchasing === pkg.id ? (
                          <CircleNotch size={16} className="animate-spin text-[#6366F1]" />
                        ) : (
                          <ArrowRight size={16} className="text-[#A0AEC0] group-hover:text-white transition-colors" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Footer hint */}
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#A0AEC0]">
                  Credits are added instantly after purchase. Secure checkout via Stripe.
                </p>
                <button
                  onClick={onClose}
                  className="text-sm text-[#A0AEC0] hover:text-white transition-colors"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default InsufficientCreditsModal;
